import { nameFields } from "./individualName";

// The mobile ownership assistant saves its structure as nested "roots" (ownership.mobileAssistantDraft)
// and a display-only diagram without boxes (assistantTree.nodes = []). These helpers turn the roots
// into tree nodes with the full chain, so the diagram, the UBO payload and the row matching use the
// structure as entered (intermediate companies kept, each % being the one held in the direct parent),
// not table rows flattened under the direct shareholder.

const clean = (value) => String(value ?? "").trim();

function clientTypeForMode(mode) {
  if (mode === "pic") return "PIC";
  if (mode === "holdingActive") return "HoldingActive";
  if (mode === "holdingPassive") return "HoldingPassive";
  if (mode === "quoted") return "Quoted Company";
  if (mode === "trust") return "Trust";
  if (mode === "foundation") return "Foundation";
  return "Non Quoted Operational Company";
}

function nodeTypeFor(node) {
  const type = clean(node?.type).toLowerCase();
  if (type === "trust") return "Trust";
  if (type === "foundation") return "Foundation";
  if (type !== "entity") return "Individual";
  const category = clean(node?.entityCategory);
  if (category === "pic") return "PIC";
  if (category === "quoted") return "Quoted Company";
  if (category === "holding") return clean(node?.holdingKind) === "passive" ? "HoldingPassive" : "HoldingActive";
  return "Non Quoted Operational Company";
}

// Same rules as the mobile assistant: the client's shareholders carry a % unless the client is a
// PIC, passive holding or quoted; an entity's owners do unless it is a PIC or passive holding.
const clientUsesPercent = (mode) => !["pic", "holdingPassive", "quoted", "trust", "foundation"].includes(mode);
const ownersUsePercent = (node) =>
  !(clean(node?.entityCategory) === "pic" || (clean(node?.entityCategory) === "holding" && clean(node?.holdingKind) === "passive"));
const hasOwners = (node) =>
  clean(node?.type).toLowerCase() === "entity" && !["quoted", "nonQuotedOperational"].includes(clean(node?.entityCategory)) &&
  Array.isArray(node?.owners) && node.owners.length > 0;

function percentNumber(value) {
  const n = Number(clean(value).replace(/%/g, ""));
  return clean(value) !== "" && Number.isFinite(n) && n >= 0 && n <= 100 ? n : undefined;
}

export function mobileAssistantTreeNodes(roots, preliminaryMode, entityName) {
  const nodes = [{ ID: "X0", name: clean(entityName), type: clientTypeForMode(preliminaryMode), parentIDs: [] }];
  const flowLines = [];
  const seen = new Set(["X0"]);

  function walk(list, parentChain, usesPercent) {
    (Array.isArray(list) ? list : []).forEach((node) => {
      const id = clean(node?.id);
      const type = nodeTypeFor(node);
      if (!id || seen.has(id) || clean(node?.type).toLowerCase() === "legalarrangement") return;
      seen.add(id);
      const percent = usesPercent ? percentNumber(node?.ownershipPercent) : undefined;
      nodes.push({
        ID: id,
        ...nameFields(node),
        name: clean(node?.name),
        type,
        parentIDs: parentChain,
        ...(percent !== undefined ? { percent } : {}),
        nationality: type === "Individual" ? clean(node?.nationality) : "",
        countryOfResidence: clean(node?.countryOfResidence),
      });
      const label = clean(node?.name).replace(/"/g, "'") || "…";
      flowLines.push(`${parentChain[0]} ${percent !== undefined ? `-->|"${percent}%"|` : "-->"} ${id}("${label}")`);
      if (hasOwners(node)) walk(node.owners, [id, ...parentChain], ownersUsePercent(node));
    });
  }

  walk(roots, ["X0"], clientUsesPercent(preliminaryMode));
  return { nodes, flowLines };
}

// The saved diagram, with boxes built from the mobile assistant's structure when it has none.
export function ownershipTreeWithMobileNodes(ownership) {
  const tree = ownership?.assistantTree;
  const roots = ownership?.mobileAssistantDraft?.roots;
  if (!tree || typeof tree !== "object" || !tree.mermaid || tree.source === "manualEntry") return tree;
  if ((Array.isArray(tree.nodes) && tree.nodes.length) || !Array.isArray(roots) || !roots.length) return tree;
  const mode = clean(ownership?.mobileAssistantDraft?.preliminaryMode);
  // The client's box is named by the diagram builder (account holder name).
  const { nodes, flowLines } = mobileAssistantTreeNodes(roots, mode, "");
  return { ...tree, nodes, flowLines, initialAHType: clientTypeForMode(mode), source: "mobileAssistant" };
}
